import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useAuth } from "../contexts/AuthContext";
import logo from 'figma:asset/e5c375aeb9d5459e76d1f4b4579b4d2ffbb0055e.png';
import { ForgotPasswordDialog } from "./ForgotPasswordDialog";
import { toast } from "sonner";

export function LoginDialog() {
  const { showLoginDialog, setShowLoginDialog, login, setShowRegisterDialog, sendEmailCode, loginWithEmailCode, lastAuthError } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<"password" | "code">("password");
  const [emailCode, setEmailCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [errors, setErrors] = useState({ email: "", password: "" });

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  // 邮箱格式验证
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 重置错误
    setErrors({ email: "", password: "" });
    
    // 验证邮箱格式
    if (!validateEmail(email)) {
      toast.error("邮箱格式错误", {
        description: "请输入有效的邮箱地址"
      });
      setErrors(prev => ({ ...prev, email: "邮箱格式不正确" }));
      return;
    }

    if (mode === "password") {
      if (password.length < 6) {
        toast.error("密码错误", {
          description: "密码长度至少为6位"
        });
        setErrors(prev => ({ ...prev, password: "密码长度至少为6位" }));
        return;
      }
      const success = await login(email, password);
      if (success) {
        toast.success("登录成功", {
          description: `欢迎回来！${email}`
        });
        setEmail("");
        setPassword("");
        setErrors({ email: "", password: "" });
      } else {
        toast.error("登录失败", {
          description: lastAuthError || "邮箱或密码错误，请检查后重试"
        });
      }
      return;
    }

    if (mode === "code") {
      const code = emailCode.trim();
      if (code.length !== 6) {
        toast.error("验证码错误", { description: "请输入邮箱中的6位验证码" });
        return;
      }
      const success = await loginWithEmailCode(email, code);
      if (success) {
        toast.success("登录成功", { description: `欢迎回来！${email}` });
        setEmail("");
        setEmailCode("");
        setCodeSent(false);
        setCooldown(0);
      } else {
        toast.error("登录失败", { description: lastAuthError || "验证码登录失败" });
      }
      return;
    }
  };

  return (
    <>
      <Dialog open={showLoginDialog} onOpenChange={setShowLoginDialog}>
        <DialogContent 
          className="sm:max-w-[425px] bg-white border border-blue-500 rounded-none shadow-none p-0"
          style={{
            outline: 'none',
          }}
        >
          <div className="p-8">
            {/* Logo */}
            <div className="mb-8 flex justify-center">
              <DialogTitle className="sr-only">登录</DialogTitle>
              <DialogDescription className="sr-only">登录以使用工具</DialogDescription>
              <div className="ml-2">
                <img 
                  src={logo}
                  alt="dado logo"
                  className="h-14 object-contain"
                />
              </div>
            </div>

            {/* 登录表单 */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-black">
                  邮箱
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="border-blue-500 rounded-none focus-visible:ring-blue-500 bg-white"
                />
                {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
              </div>

              {mode === "password" && (
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="password" className="text-black">
                      密码
                    </Label>
                    <div className="space-x-3">
                      <button
                        type="button"
                        className="text-xs text-blue-500 hover:underline"
                        onClick={() => {
                          setShowLoginDialog(false);
                          setShowForgotPassword(true);
                        }}
                      >
                        忘记密码？
                      </button>
                      <button
                        type="button"
                        className="text-xs text-blue-500 hover:underline"
                        onClick={() => setMode("code")}
                      >
                        使用验证码登录
                      </button>
                    </div>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="border-blue-500 rounded-none focus-visible:ring-blue-500 bg-white"
                  />
                  {errors.password && <p className="text-red-500 text-sm">{errors.password}</p>}
                </div>
              )}

              {mode === "code" && (
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="emailCode" className="text-black">
                      验证码
                    </Label>
                    <button
                      type="button"
                      className="text-xs text-blue-500 hover:underline"
                      onClick={() => setMode("password")}
                    >
                      使用密码登录
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      id="emailCode"
                      type="text"
                      placeholder="输入邮箱中的6位验证码"
                      value={emailCode}
                      onChange={(e) => setEmailCode(e.target.value)}
                      className="border-blue-500 rounded-none focus-visible:ring-blue-500 bg-white"
                    />
                    <button
                      type="button"
                      className="border border-blue-500 text-blue-500 px-3 rounded-none hover:bg-blue-50"
                      onClick={async () => {
                        if (!validateEmail(email)) {
                          toast.error("邮箱格式错误", { description: "请输入有效的邮箱地址" });
                          return;
                        }
                        if (cooldown > 0) return;
                        const ok = await sendEmailCode(email);
                        if (ok) {
                          setCodeSent(true);
                          setCooldown(60);
                          toast.success("验证码已发送", { description: "请在邮箱中查看6位验证码" });
                        } else {
                          toast.error("验证码发送失败", { description: lastAuthError || "请稍后重试" });
                        }
                      }}
                    >
                      {cooldown > 0 ? `重新发送(${cooldown}s)` : (codeSent ? "重新发送" : "发送验证码")}
                    </button>
                  </div>
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-white border border-blue-500 text-blue-500 py-3 hover:bg-blue-50 transition-colors flex items-center justify-center"
                style={{ fontFamily: "'Noto Sans SC', sans-serif" }}
              >
                登录
              </button>

              {/* 注册提示 */}
              <p className="text-center text-gray-500 text-sm mt-4">
                还没有账号？{" "}
                <button
                  type="button"
                  className="text-blue-500 hover:underline"
                  onClick={() => {
                    setShowLoginDialog(false);
                    setTimeout(() => {
                      setShowRegisterDialog(true);
                    }, 100);
                  }}
                >
                  立即注册
                </button>
              </p>
            </form>
          </div>
        </DialogContent>
      </Dialog>
      
      <ForgotPasswordDialog 
        open={showForgotPassword} 
        onOpenChange={setShowForgotPassword}
      />
    </>
  );
}
